import { useState } from 'react';
import { Button, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import { useTheme } from '@mui/material/styles';
import AreaLineChart from 'components/Widgets/AreaLineChart';
import AreaChart from 'components/Widgets/AreaChart';
import MultiLineChartWidget from 'components/Widgets/MultiLineChartWidget';
import MetricAreaLineChart from 'components/Widgets/MetricAreaLineChart';
import AreaTrendChart from 'components/Widgets/AreaTrendChart';
import SimpleDropdownFilter from 'components/Dropdown/SimpleDropdownFilter';
import AreaTrendComparisonChart, { MonthlyTrendApiData } from 'components/Widgets/AreaTrendComparisonChart';

const data = [
  { month: 'Jan', product01: 120, product02: 80, product03: 200, avgTrend: 150 },
  { month: 'Feb', product01: 100, product02: 60, product03: 180, avgTrend: 130 },
  { month: 'Mar', product01: 140, product02: 120, product03: 160, avgTrend: 140 },
  { month: 'Apr', product01: 160, product02: 220, product03: 140, avgTrend: 173.33 },
  { month: 'May', product01: 130, product02: 90, product03: 170, avgTrend: 130 },
];

const metricData = [
  { year: '2021', value1: 120, value2: 80, value3: 200, avgTrend: 150 },
  { year: '2022', value1: 100, value2: 60, value3: 180, avgTrend: 130 },
  { year: '2023', value1: 140, value2: 120, value3: 160, avgTrend: 140 },
  { year: '2024', value1: 160, value2: 220, value3: 140, avgTrend: 173.33 },
  { year: '2025', value1: 130, value2: 90, value3: 170, avgTrend: 130 },
];

const areaChartData = [
  { year: '2010', actual: 120, target: 80 },
  { year: '2015', actual: 100, target: 60 },
  { year: '2020', actual: 140, target: 120 },
  { year: '2025', actual: 160, target: 220 },
  { year: '2030', actual: 130, target: 90 },
];

const lineChartdata = [
  { name: 'FEB', northAmerica: 780, international: 580 },
  { name: 'MAR', northAmerica: 360, international: 790 },
  { name: 'APR', northAmerica: 410, international: 580 },
  { name: 'JUL', northAmerica: 260, international: 460 },
  { name: 'SEP', northAmerica: 310, international: 580 },
  { name: 'OCT', northAmerica: 510, international: 790 },
  { name: 'NOV', northAmerica: 480, international: 620 },
];

const chartData = [
  { month: 'DEC', incidents: 8200, trend: 7800 },
  { month: 'NOV', incidents: 6300, trend: 3400 },
  { month: 'OCT', incidents: 5100, trend: 3800 },
  { month: 'SEP', incidents: 7200, trend: 4000 },
  { month: 'AUG', incidents: 8800, trend: 2400 },
  { month: 'JUL', incidents: 5700, trend: 2800 },
];

const areaChartData2 = {
    "kbArticlePublicationTrend": [
        {
            "Count": 2633,
            "YearMonth": "2026-01"
        },
        {
            "Count": 516,
            "YearMonth": "2026-02"
        },
        {
            "Count": 1739,
            "YearMonth": "2026-04"
        }
    ],
    "kbArticleDAIPublicationTrend": [
        {
            "Count": 4,
            "YearMonth": "2026-01"
        },
        {
            "Count": 0,
            "YearMonth": "2026-02"
        },
        {
            "Count": 0,
            "YearMonth": "2026-04"
        }
    ]
}

export default function LineChartsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = theme.palette;

  const [chartPreference, setChartPreference] = useState('all');

  const [knowledgeBaseData, setKnowledgeBaseData] = useState<MonthlyTrendApiData | null>(areaChartData2);
  const [kbArticleLoading, setKbArticleLoading] = useState(false);

  const handleFilterChange = (option: string) => {
    console.warn('Option...', option);
  };

  return (
    <PageWrapper>
      <PageHeader title="Area - Line Charts Demo">
        <Button variant="pepwiseSecondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </PageHeader>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} sm={12} md={6}>
          <AreaLineChart
            title="Knowledge Base Articles"
            subTitle="Product by Month"
            data={data}
            xKey="month"
            isCost={true}
            seriesConfig={[
              {
                key: 'product01',
                name: 'Product 01',
                color: colors.widget.colors.color7 ?? '#1F4E79',
              },
              {
                key: 'product02',
                name: 'Product 02',
                color: colors.widget.colors.color8 ?? '#3FA9F5',
              },
              {
                key: 'product03',
                name: 'Product 03',
                color: colors.widget.colors.color9 ?? '#A6C8E1',
              },
              {
                key: 'avgTrend',
                name: 'Average Trend',
                color: colors.widget.colors.trendLine ?? '#F57600',
                variant: 'line',
              },
            ]}
            xAxisLabel="Month"
            isLoading={false}
            hasTooltip={true}
            tooltipLabel="This is an example of an area line chart showing knowledge base articles over months for different products."
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <AreaChart
            title="Knowledge Base Articles"
            data={areaChartData}
            xKey="year"
            isCost={false}
            seriesConfig={[
              { key: 'actual', name: 'Actual', color: '#FFC994' },
              { key: 'target', name: 'Target', color: '#3BA3E0' },
            ]}
            isLoading={false}
            hasTooltip={true}
            tooltipLabel="This is an example of an area chart showing knowledge base articles over years."
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <MultiLineChartWidget
            title="Monthly Comparison Chart for Regional"
            subTitle="Region"
            data={lineChartdata}
            xKey="name"
            xAxisLabel="Month"
            series={[
              {
                key: 'northAmerica',
                name: 'North America',
                color: '#FF7A1A',
              },
              {
                key: 'international',
                name: 'International',
                color: '#00B578',
              },
            ]}
            isCost={true}
            isLoading={false}
            hasTooltip={true}
            tooltipLabel="This is an example of a multi-line chart comparing regions over months."
            showGridLines={false}
            lastUpdated="11-25-2025"
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <MetricAreaLineChart
            title="Incident Intake Channel Metrics"
            value={5460000}
            data={metricData}
            xKey="year"
            isCost={true}
            seriesConfig={[
              {
                key: 'value1',
                name: 'Value 01',
                color: '#00B4C5',
              },
              {
                key: 'value2',
                name: 'Value 02',
                color: '#00BF7D',
              },
              {
                key: 'value3',
                name: 'Value 03',
                color: '#8BABF1',
              },
              {
                key: 'avgTrend',
                name: 'Average Trend',
                color: '#F57600',
                variant: 'line',
              },
            ]}
            isLoading={false}
            hasTooltip={true}
            tooltipLabel="This is an example of an metric area line chart showing Incident Intake Channel Metrics over years."
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <AreaTrendChart
            title="Incidents"
            data={chartData}
            xKey="month"
            xAxisLabel="Month"
            isCost={true}
            hasTooltip
            tooltipLabel="Incidents vs Trend by month"
            seriesConfig={[
              { key: 'incidents', name: 'Incidents', color: '#8CB4FF', variant: 'area' },
              { key: 'trend', name: 'Trend', color: '#F57600', variant: 'line' },
            ]}
          >
            <SimpleDropdownFilter
              value={chartPreference}
              setValue={setChartPreference}
              optionsList={['Platform A', 'Platform B']}
              handleChange={handleFilterChange}
              showAllOption={true}
              allLabel="All Platforms"
              disabled={false}
              boxProps={{
                sx: { minWidth: '20px', position: 'static' },
              }}
            />
          </AreaTrendChart>
        </Grid>

        <Grid item xs={12} sm={12} md={12}>
          <AreaTrendComparisonChart
            title='Knowledge Base Articles (MOM)'
            subTitle="Product by Month"
            isLoading={kbArticleLoading}
            data={knowledgeBaseData}
            seriesConfig={[
              {
                dataKey: 'kbArticlePublicationTrend',
                name: 'KB Articles',
                color: '#12C99B',
                yAxisIndex: 0
              },
              {
                dataKey: 'kbArticleDAIPublicationTrend',
                name: 'D&AI KB Articles',
                color: '#08B8C9',
                yAxisIndex: 1
              }
            ]}
            valueType="number"
            rightValueType="number"
            leftYAxisName="KB Articles"
            rightYAxisName="D&AI KB Articles"
            hasTooltip
            tooltipLabel="Knowledge Base Articles (MOM)"
            showRightYAxis
            showTrendLine={false}
          />
        </Grid>
      </Grid>
    </PageWrapper>
  );
}
